"use client";

import { useMemo } from "react";

import { useOrganization } from "../organizationContext";

import { useSession } from "./client";
//import { extractUserRole } from "./types";

/**
 * Platform and Business Permission Hooks
 *
 * Consolidates all authentication and authorization checks into two primary hooks:
 * 1. usePlatformPermissions() - Platform admin permissions (User.role + platform org)
 * 2. useBusinessPermissions() - B2C organization permissions (Business→Org→Member→Role)
 *
 * Dual Permission System:
 * - **System-level**: User.role field for Better Auth admin plugin features
 *   (impersonate, ban, removeUser, session management)
 * - **Business-level**: Organization→Member→Role for ApartmentDibs features
 *   (listings, employees, business settings)
 *
 * Platform employees have BOTH:
 * - User.role="admin" (for Better Auth admin features)
 * - Platform org membership with permissions (debug, config.manage, etc.)
 *
 * **IMPORTANT: isPlatformEmployee vs hasPermission**
 *
 * Use `hasPermission(permission)` for granular permission checks:
 * - Checks if user has a specific capability (e.g., "users.impersonate")
 * - Respects role hierarchy and permission grants
 * - Returns false for permissions user doesn't have
 *
 * Use `isPlatformEmployee` (or `isPlatformAdmin`) for broad access checks:
 * - Boolean flag indicating platform membership + admin status
 * - Use when bypassing normal authorization (e.g., accessing all businesses)
 * - Typically combined with more specific permission checks
 *
 * Example:
 * ```ts
 * // ✅ CORRECT: Granular permission check
 * if (hasPermission("users.impersonate")) {
 *   // Show impersonate button
 * }
 *
 * // ✅ CORRECT: Broad access check with permission verification
 * if (isPlatformEmployee && hasPermission("businesses.manage")) {
 *   // Access all businesses
 * }
 *
 * // ❌ WRONG: Using isPlatformEmployee without permission check
 * if (isPlatformEmployee) {
 *   // Overly broad - not all platform members can impersonate!
 * }
 * ```
 *
 * @see ADR-002: Organization Plugin for Business Multi-Tenancy
 * @see ADR-003: Auth Guard Component Pattern
 * @see ADR-009: Admin Impersonation Principles
 */

/**
 * Platform-level permissions for system administrators
 *
 * These permissions are granted to members of the "platform" organization and control
 * access to platform-wide administrative features. Permissions are role-based:
 *
 * **Permission Tiers**:
 * - All Platform Members: `debug`
 * - Customer Support + User.role="admin": `users.impersonate`, `sessions.manage`
 * - Admin/Owner: `config.manage`, `feature-flags.manage`, `businesses.manage`, `listings.moderate`
 * - Admin/Owner + User.role="admin": All Better Auth admin features
 *
 * **Dual Authorization Model**:
 * Some permissions require BOTH platform org membership AND User.role="admin":
 * - `users.impersonate` - Requires User.role="admin" + permission check
 * - `users.ban` - Requires User.role="admin" (Better Auth admin plugin)
 * - `users.remove` - Requires User.role="admin" (Better Auth admin plugin)
 * - `sessions.manage` - Requires User.role="admin" + permission check
 *
 * @example
 * ```ts
 * const { hasPermission } = usePlatformPermissions();
 *
 * if (hasPermission("debug")) {
 *   // Show debug toolbar (all platform members)
 * }
 *
 * if (hasPermission("users.impersonate")) {
 *   // Show impersonate button (requires User.role="admin")
 * }
 * ```
 *
 * @see usePlatformPermissions
 * @see ADR-009: Admin Impersonation Principles
 */
export type PlatformPermission =
  | "debug" // View debug information, inspect data, access /admin pages
  | "config.manage" // Manage platform configuration
  | "feature-flags.manage" // Toggle feature flags
  | "users.impersonate" // Impersonate users (requires User.role="admin")
  | "users.ban" // Ban/unban users (requires User.role="admin")
  | "users.remove" // Permanently delete users (requires User.role="admin")
  | "sessions.manage" // View and revoke user sessions (requires User.role="admin")
  | "businesses.manage" // Manage all businesses on platform
  | "listings.moderate"; // Moderate all listings on platform

/**
 * Business-level permissions for B2C organizations
 *
 * These permissions are granted based on Organization→Member→Role relationships
 * and control access to business-specific features within a single organization.
 * Each business (via its Organization) has independent permission scoping.
 *
 * **Permission Tiers by Member Role**:
 * - All Members: `listings.view`, `employees.view`
 * - Member+: `listings.create`, `listings.edit`
 * - Admin+: `listings.delete`, `employees.invite`, `employees.manage`, `business.edit`
 * - Owner Only: `business.delete`
 *
 * **Multi-Tenancy Model**:
 * - User can be a member of multiple organizations with different roles
 * - Permissions apply only to the active organization context
 * - Switching organizations changes available permissions
 *
 * **Permission Flow**:
 * 1. User authenticates (Better Auth session)
 * 2. User selects active organization (organizationContext)
 * 3. Member role determines permissions for that organization
 * 4. Business features check permissions via useBusinessPermissions()
 *
 * @example
 * ```ts
 * const { hasPermission, organizationRole } = useBusinessPermissions();
 *
 * if (hasPermission("listings.create")) {
 *   // Show create listing button (member+)
 * }
 *
 * if (hasPermission("business.delete")) {
 *   // Show delete business button (owner only)
 * }
 * ```
 *
 * @see useBusinessPermissions
 * @see ADR-002: Organization Plugin for Business Multi-Tenancy
 */
export type BusinessPermission =
  | "listings.view" // View organization's listings
  | "listings.create" // Create new listings
  | "listings.edit" // Edit existing listings
  | "listings.delete" // Delete listings
  | "employees.view" // View organization employees
  | "employees.invite" // Invite new employees
  | "employees.manage" // Manage employee roles and permissions
  | "business.edit" // Edit business profile
  | "business.delete"; // Delete business (owner only)

/**
 * Checks if a user can access a specific business
 *
 * Determines business access based on Organization→Member relationships and
 * platform admin status. A user can access a business if they are either:
 * 1. A member of the business's organization (any role)
 * 2. A platform admin with `businesses.manage` permission
 *
 * **Access Logic**:
 * - Business Member: Direct organization membership grants access
 * - Platform Admin: Can access all businesses for moderation/support
 * - Regular Users: No access to businesses they're not members of
 *
 * @param organizationId - The organization ID to check access for
 * @param userMemberships - Array of user's organization memberships
 * @param isPlatformEmployee - Whether user is a platform admin with business management permissions
 * @returns `true` if user can access the business, `false` otherwise
 *
 * @example
 * ```ts
 * const { organizations, activeBusiness } = useOrganization();
 * const { hasPermission } = usePlatformPermissions();
 * const isPlatformEmployee = hasPermission("businesses.manage");
 *
 * if (canUserAccessBusiness(organizationId, organizations, isPlatformEmployee)) {
 *   // Show business dashboard
 * }
 * ```
 *
 * @remarks
 * - Used primarily in route guards and data fetching logic
 * - Platform employees bypass organization membership checks
 * - Does NOT check for specific permissions within the business
 * - For permission checks, use `useBusinessPermissions()` after access is confirmed
 *
 * @see useBusinessPermissions
 * @see usePlatformPermissions
 * @see ADR-002: Organization Plugin for Business Multi-Tenancy
 */
export function canUserAccessBusiness(
  organizationId: string | null | undefined,
  userMemberships: Array<{ organizationId: string }>,
  isPlatformEmployee: boolean,
): boolean {
  if (!organizationId) return false;

  // Platform employees can access all businesses
  if (isPlatformEmployee) return true;

  // Check if user is a member of any organization
  // Note: In the current data model, Business has 1:1 relationship with Organization
  // so we check if user is a member of the business's organization
  return userMemberships.some(
    (membership) => membership.organizationId === organizationId,
  );
}

/**
 * Platform permissions hook
 *
 * Combines User.role check with platform organization membership to determine
 * platform-level permissions. Used for /admin pages and Better Auth admin features.
 *
 * Permission model:
 * - User.role="admin" → Better Auth admin plugin features (impersonate, ban, etc.)
 * - Platform org membership → ApartmentDibs platform permissions (debug, config, etc.)
 * - Both required for full platform admin access
 *
 * @returns Object with platform permission checks and loading states
 *
 * @example
 * ```tsx
 * const { hasPermission, isAdmin, isPlatformMember, isLoading } = usePlatformPermissions();
 *
 * if (hasPermission("debug")) {
 *   // Show debug UI
 * }
 *
 * if (hasPermission("users.impersonate")) {
 *   // Show impersonate button
 * }
 * ```
 */
export function usePlatformPermissions() {
  const { data: session, isPending } = useSession();
  //const { organizations } = useOrganization();

  // Check User.role field for Better Auth admin plugin
  const userRoles = session?.user?.role?.split(",") ?? [];
  const isPlatformAdmin = userRoles.includes("admin");

  // Check platform organization membership using organizationContext
  //const isPlatformMember = organizations.some(
  //  (org) => org.organizationSlug === "platform",
  //);

  // // Determine platform permissions based on org role
  // const permissions = useMemo<Set<PlatformPermission>>(() => {
  //   const perms = new Set<PlatformPermission>();

  //   if (!isPlatformMember) {
  //     return perms;
  //   }

  //   // Base permissions for all platform members
  //   perms.add("debug");

  //   // Customer Support permissions (impersonation only)
  //   // Customer support can impersonate users for troubleshooting without full admin access
  //   if (platformRole === "customer-support" && isAdmin) {
  //     perms.add("users.impersonate");
  //     perms.add("sessions.manage");
  //   }

  //   // Admin/Owner permissions
  //   if (platformRole === "admin" || platformRole === "owner") {
  //     perms.add("config.manage");
  //     perms.add("feature-flags.manage");
  //     perms.add("businesses.manage");
  //     perms.add("listings.moderate");
  //   }

  //   // Better Auth admin plugin features (require User.role="admin")
  //   // Admin/Owner roles get full Better Auth admin capabilities
  //   if (isAdmin && (platformRole === "admin" || platformRole === "owner")) {
  //     perms.add("users.impersonate");
  //     perms.add("users.ban");
  //     perms.add("users.remove");
  //     perms.add("sessions.manage");
  //   }

  //   return perms;
  // }, [isPlatformMember, platformRole, isAdmin]);

  /**
   * Check if user has a specific platform permission
   */
  // const hasPermission = (permission: PlatformPermission): boolean => {
  //   return permissions.has(permission);
  // };

  /**
   * Check if user has ANY of the specified permissions
   */
  // const hasAnyPermission = (permissionList: PlatformPermission[]): boolean => {
  //   return permissionList.some((p) => permissions.has(p));
  // };

  /**
   * Check if user has ALL of the specified permissions
   */
  // const hasAllPermissions = (permissionList: PlatformPermission[]): boolean => {
  //   return permissionList.every((p) => permissions.has(p));
  // };

  return {
    // Permission checks
    // hasPermission,
    // hasAnyPermission,
    // hasAllPermissions,
    // permissions: Array.from(permissions),

    // User.role check (for Better Auth admin plugin)
    isPlatformAdmin,

    // Platform organization membership
    //isPlatformMember,
    //    platformRole,

    // Combined check: is user a full platform admin?
    // For now, we only check User.role="admin" since platform membership
    // check requires organization context which may not be loaded yet
    // TODO: Uncomment when we implement proper permission granularity
    // isPlatformAdmin: isPlatformAdmin && isPlatformMember,

    // Loading state
    isPending,
  };
}

/**
 * Calculate business permissions based on organization role
 *
 * Implements a hierarchical permission model where higher roles inherit
 * permissions from lower roles:
 * - Owner: All permissions (inherits from Admin)
 * - Admin: Management permissions (inherits from Member)
 * - Member: Basic CRUD permissions (inherits from base)
 * - Base: View-only permissions
 *
 * @param organizationRole - User's role in the organization (owner/admin/member)
 * @param hasActiveBusiness - Whether user has an active business context
 * @returns Set of business permissions granted to the role
 *
 * @internal
 */
function calculateBusinessPermissions(
  organizationRole: string | undefined,
  hasActiveBusiness: boolean,
): Set<BusinessPermission> {
  const perms = new Set<BusinessPermission>();

  if (!organizationRole || !hasActiveBusiness) {
    return perms;
  }

  // Base permissions: All organization members can view
  perms.add("listings.view");
  perms.add("employees.view");

  // Member+ permissions: Create and edit capabilities
  if (
    organizationRole === "member" ||
    organizationRole === "admin" ||
    organizationRole === "owner"
  ) {
    perms.add("listings.create");
    perms.add("listings.edit");
  }

  // Admin+ permissions: Management capabilities
  if (organizationRole === "admin" || organizationRole === "owner") {
    perms.add("listings.delete");
    perms.add("employees.invite");
    perms.add("employees.manage");
    perms.add("business.edit");
  }

  // Owner-only permissions: Destructive operations
  if (organizationRole === "owner") {
    perms.add("business.delete");
  }

  return perms;
}

/**
 * Business permissions hook
 *
 * Returns permissions for the active organization based on Member→Role.
 * Used for /landlord pages and B2C business features.
 *
 * Permission model:
 * - User → Member → Organization → Business
 * - Member.role determines permissions (owner > admin > member)
 * - Each organization/business has independent permissions
 *
 * @returns Object with business permission checks and loading states
 *
 * @example
 * ```tsx
 * const { hasPermission, isOwner, activeBusiness, isLoading } = useBusinessPermissions();
 *
 * if (hasPermission("listings.create")) {
 *   // Show create listing button
 * }
 *
 * if (hasPermission("employees.manage")) {
 *   // Show employee management UI
 * }
 * ```
 */
export function useBusinessPermissions() {
  const { activeOrganizationId, organizations, activeBusiness, isLoading } =
    useOrganization();

  // Get active organization details
  const activeOrganization = organizations.find(
    (org) => org.organizationId === activeOrganizationId,
  );
  const organizationRole = activeOrganization?.memberRole;

  // Check if user has any organization memberships (is a landlord)
  // Check both organizations array AND activeOrganizationId to handle loading states
  const isLandlord = organizations.length > 0 || !!activeOrganizationId;

  // Check if user has active business
  const hasActiveBusiness = !!activeBusiness;

  // Calculate permissions based on role hierarchy
  const permissions = useMemo<Set<BusinessPermission>>(
    () => calculateBusinessPermissions(organizationRole, hasActiveBusiness),
    [organizationRole, hasActiveBusiness],
  );

  /**
   * Check if user has a specific business permission
   */
  const hasPermission = (permission: BusinessPermission): boolean => {
    return permissions.has(permission);
  };

  /**
   * Check if user has ANY of the specified permissions
   */
  const hasAnyPermission = (permissionList: BusinessPermission[]): boolean => {
    return permissionList.some((p) => permissions.has(p));
  };

  /**
   * Check if user has ALL of the specified permissions
   */
  const hasAllPermissions = (permissionList: BusinessPermission[]): boolean => {
    return permissionList.every((p) => permissions.has(p));
  };

  return {
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions: Array.from(permissions),

    // Organization details
    activeOrganizationId,
    activeBusiness,
    organizationRole,

    // Role checks
    isOwner: organizationRole === "owner",
    isAdmin: organizationRole === "admin" || organizationRole === "owner",
    isMember: !!organizationRole,

    // Landlord status (has any organization membership)
    isLandlord,

    // Active business status
    hasActiveBusiness,

    // Loading state
    isLoading,
  };
}

/**
 * Combined permissions hook for components that need both platform and business permissions
 *
 * @example
 * ```tsx
 * const { platform, business } = usePermissions();
 *
 * if (platform.hasPermission("debug")) {
 *   // Show debug info
 * }
 *
 * if (business.hasPermission("listings.create")) {
 *   // Show create button
 * }
 * ```
 */
// export function usePermissions() {
//   const platform = usePlatformPermissions();
//   const business = useBusinessPermissions();

//   return {
//     platform,
//     business,
//     isLoading: platform.isLoading || business.isLoading,
//   };
// }
